import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, Loader2, Lock, XCircle, Zap } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-zinc-100 placeholder-zinc-500 outline-none transition-colors focus:border-neon-lime/60 focus:ring-1 focus:ring-neon-lime/40";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: next });
      setDone(true);
      toast.success("Password reset. Log in with your new password.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4" data-testid="reset-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0E1117] p-8"
      >
        <div className="mb-8 text-center">
          <Zap className="mx-auto h-7 w-7 text-neon-lime" fill="currentColor" />
          <h1 className="mt-4 font-display text-xl font-bold uppercase tracking-tight">Set a New Password</h1>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">After Dark — Admin</p>
        </div>
        {!token ? (
          <div className="text-center" data-testid="reset-invalid">
            <XCircle className="mx-auto h-10 w-10 text-red-400" />
            <p className="mt-4 text-sm text-zinc-400">This reset link is missing its token. Request a fresh one from the login page.</p>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="reset-success">
            <BadgeCheck className="mx-auto h-10 w-10 text-neon-lime" />
            <p className="mt-4 text-sm text-zinc-400">Password updated.</p>
            <a href="/admin" data-testid="reset-login-link"
              className="mt-6 inline-block rounded-full bg-neon-lime px-8 py-3 font-mono text-xs font-bold uppercase tracking-[0.25em] text-black transition-all hover:-translate-y-0.5">
              Log In
            </a>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <input type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)}
              placeholder="New password (8+ chars)" data-testid="reset-new-input" className={inputCls} />
            <input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm new password" data-testid="reset-confirm-input" className={inputCls} />
            <button type="submit" disabled={busy} data-testid="reset-submit-button"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-neon-lime py-3.5 font-mono text-xs font-bold uppercase tracking-[0.25em] text-black transition-all hover:-translate-y-0.5 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              {busy ? "Resetting" : "Reset Password"}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
